import _ from 'lodash';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled, { css } from 'styled-components';

import { TargetIcon } from '@treams/treams-icons';
import { Card, Empty, Heading, Highlight, Paragraph, Radio, Skeleton } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import QuillText from 'packages/kakapo-ui-kit/components/editors/QuillText';

const Container = styled(Box)<{ grid?: boolean }>`
	${(props) =>
		props.grid &&
		css`
			display: grid;
			grid-template-columns: repeat(auto-fit, 300px);
			gap: 16px;
		`}
`;

const TemplateCard = Skeleton(
	function TemplateCard(props: {
		query?: string;
		template: graphql.GoalTemplate | null;
		checked?: boolean;
		onChange: () => void;
	}) {
		const { t } = useTranslation();

		const { localize } = useLocalize();

		return (
			<Card onClick={() => props.onChange()} selected={props.checked}>
				<Flex alignItems="center">
					<Radio
						flexGrow={0}
						flexShrink={0}
						mr={3}
						checked={props.checked}
						onChange={() => props.onChange()}
					/>
					<Box flexGrow={1} flexShrink={1}>
						<Heading>
							<Highlight query={props.query}>
								{props.template
									? localize(props.template.title).text
									: t('goals.new.template.scratch.title')}
							</Highlight>
						</Heading>
						<Paragraph mt={1}>
							{props.template ? (
								<QuillText quill={localize(props.template.description).text} />
							) : (
								t('goals.new.template.scratch.description')
							)}
						</Paragraph>
					</Box>
				</Flex>
			</Card>
		);
	},
	function TemplateCardSkeleton() {
		return (
			<Card.Skeleton>
				<Heading.Skeleton />
				<Paragraph.Skeleton mt={1} lines={1} />
			</Card.Skeleton>
		);
	},
);

function JobPosition(props: {
	grid?: boolean;
	position: graphql.JobPosition;
	query?: string;
	template?: graphql.GoalTemplate | null;
	onChange: (template: graphql.GoalTemplate) => void;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const level = props.position.jobLevel && localize(props.position.jobLevel.title).text;
	const title = localize(props.position.jobTitle.title).text;

	return (
		<Box>
			<Paragraph mb={2}>
				{t('goals.new.template.section.title', { name: level ? `${level} ${title}` : title })}
			</Paragraph>
			<Container grid={props.grid}>
				{[...props.position.jobTitle.goalTemplates.edges]
					.sort(({ node: a }, { node: b }) =>
						localize(a.title).text.localeCompare(localize(b.title).text),
					)
					.map(({ node: template }) => (
						<Box key={template.id} mb={props.grid ? 0 : 2}>
							<TemplateCard
								query={props.query}
								template={template as graphql.GoalTemplate}
								checked={template.id === props.template?.id}
								onChange={() => props.onChange(template as graphql.GoalTemplate)}
							/>
						</Box>
					))}
			</Container>
			{props.position.jobTitle.goalTemplates.edges.length === 0 && (
				<Skeleton.Empty
					contents={
						<Empty
							title={t('goals.new.template.empty.title')}
							icon={<TargetIcon />}
							description={t('goals.new.template.empty.description')}
						/>
					}
				>
					<Skeleton.Repeat count={3}>
						<Box mb={2}>
							<TemplateCard.Skeleton />
						</Box>
					</Skeleton.Repeat>
				</Skeleton.Empty>
			)}
		</Box>
	);
}

function StartFromScratch(props: {
	grid?: boolean;
	query?: string;
	template?: graphql.GoalTemplate | null;
	onChange: (template: graphql.GoalTemplate | null) => void;
}) {
	const { t } = useTranslation();

	return (
		<Box>
			<Paragraph mb={2}>{t('goals.new.template.section.scratch.title')}</Paragraph>
			<Container grid={props.grid}>
				<TemplateCard
					query={props.query}
					template={null}
					checked={props.template === null}
					onChange={() => props.onChange(null)}
				/>
			</Container>
		</Box>
	);
}

export function useRelatedGoalTemplates(id: string) {
	const templates = graphql.useUserGoalTemplatesQuery({ variables: { id } });
	const { localize } = useLocalize();

	if (!templates.data) {
		return undefined;
	}

	const related = _.sortBy(
		templates.data?.user.jobPositions ?? [],
		(position) => localize(position.jobTitle.title).text,
	);

	return related;
}

export default function GoalTemplateSelector(props: {
	grid?: boolean;
	userID: string;
	query?: string;
	template?: graphql.GoalTemplate | null;
	onChange: (template: graphql.GoalTemplate | null) => void;
	onLoad?: (templates: graphql.GoalTemplate[]) => void;
}) {
	const related = useRelatedGoalTemplates(props.userID);

	const { onLoad } = props;
	const isRelated = Boolean(related);

	useEffect(() => {
		if (!related) {
			return;
		}

		onLoad?.(
			related.flatMap(
				(position) =>
					position.jobTitle.goalTemplates.edges.map(({ node }) => node) as graphql.GoalTemplate[],
			),
		);
	}, [isRelated, related, onLoad]);

	return related ? (
		<>
			{related.map((position) => (
				<Box key={position.id} mb={3}>
					<JobPosition
						position={position as graphql.JobPosition}
						template={props.template}
						onChange={props.onChange}
						query={props.query}
						grid={props.grid}
					/>
				</Box>
			))}
			<StartFromScratch
				template={props.template}
				onChange={props.onChange}
				query={props.query}
				grid={props.grid}
			/>
		</>
	) : (
		<Skeleton.Repeat count={3}>
			<Box mb={2}>
				<TemplateCard.Skeleton />
			</Box>
		</Skeleton.Repeat>
	);
}
